// FAA ATS Route centerlines (airways) layer family.
//
// Victor / Jet / RNAV T & Q airways. Color-coded by ROUTE_CLASS property
// (derived server-side):
//   VICTOR  → cyan  (#06b6d4)
//   JET     → purple (#a855f7)
//   RNAV_LO → light blue (#7dd3fc)
//   RNAV_HI → violet (#8b5cf6)
// One GeoJSON source drives two layers: a color-coded center line and an
// along-line ident label at zoom ≥ 8. Both layers are mounted with
// visibility: 'none' (lazy toggle). Hidden by default; Aero panel drives
// the toggle.
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 3 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids,
// minzoom, and layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addAirwaysLayers(map: MlMap): void {
  if (!map.getSource('airways')) {
    map.addSource('airways', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'airways-line',
      type: 'line',
      source: 'airways',
      layout: {
        visibility: 'none',
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': [
          'match', ['get', 'ROUTE_CLASS'],
          'VICTOR',   '#06b6d4',
          'JET',      '#a855f7',
          'RNAV_LO',  '#7dd3fc',
          'RNAV_HI',  '#8b5cf6',
          /* default */ '#6b7280',
        ],
        'line-width': [
          'interpolate', ['linear'], ['zoom'],
          6, 0.8,
          10, 1.4,
          13, 2.0,
        ],
        'line-opacity': 0.8,
      },
    });
    map.addLayer({
      id: 'airways-label',
      type: 'symbol',
      source: 'airways',
      minzoom: 8,
      layout: {
        visibility: 'none',
        'symbol-placement': 'line',
        'text-field': ['get', 'IDENT'],
        'text-size': 9,
        'text-font': ['Noto Sans Regular'],
        'text-offset': [0, -0.6],
      },
      paint: {
        'text-color': '#e2e8f0',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.0,
      },
    });
  }
}

registerLayerFamily({
  id: 'airways',
  label: 'Airways',
  sourceId: 'airways',
  layerIds: ['airways-line', 'airways-label'],
  mount: (map) => addAirwaysLayers(map),
  visibilitySelector: (l) => l.airways,
  applyState: (map) => {
    if (!map.getLayer('airways-line')) return;
    const show = useLayerVisibilityStore.getState().layers.airways;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('airways-line', 'visibility', v);
    map.setLayoutProperty('airways-label', 'visibility', v);
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('airways');
    if (!spec) return;
    markOverlayLoaded('airways');
    return refreshOverlay(map, spec);
  },
  bboxRefresh: (map) => {
    if (!useLayerVisibilityStore.getState().layers.airways) return;
    const spec = OVERLAY_BY_ID.get('airways');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
